import { useState } from 'react';
import { ArrowTopRightIcon, BarChartIcon, Cross2Icon, StarIcon } from '@radix-ui/react-icons';
import { Button, Badge, Dialog } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import { isCurrentUserPremium, userIsFitPlayerProfile } from 'helpers/authentication';
import { pushEventWithProperties } from 'helpers/crmUtils';
import { getSuperSimulationNextRankChange } from 'helpers/ranks';
import { openInExternalUrl } from 'helpers/utils';
import Analytics from 'ui/design-base/Analytics';
import { clear, compute } from 'store/simulator/simulatorSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import ModalNoCard from './ModalNoCard';

export const SuperSimulationRankBox: React.FC<{
  isMatchesPage: boolean;
  superSimulationModalOpen?: boolean;
  setSuperSimulationModalOpen?: (_b: boolean) => void;
  playerId?: number;
}> = ({ isMatchesPage, superSimulationModalOpen, setSuperSimulationModalOpen, playerId }) => {
  const simulatorState = useAppSelector((state) => state.simulator.simulator);
  const [modalNoCardOpen, setModalNoCardOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const onClick = () => {
    if (!isMatchesPage) {
      navigate(`${location.pathname.length < 2 ? 'profilo' : location.pathname}/partite#super-simulazione-classifica`);
    } else {
      if (simulatorState?.isSuperSimulation) {
        onClearSuperSimulation();
      } else {
        Analytics.sendEvent({
          category: 'Profile Interaction',
          action: 'Rank Super Simulation',
          label: 'Player Profile',
        });
        if (isCurrentUserPremium()) {
          pushEventWithProperties('TT - Premium Usage', {
            type: 'supersimulata',
          });
        } else {
          pushEventWithProperties('TT - Paywall', {
            type: 'supersimulata',
          });
        }

        if (setSuperSimulationModalOpen) setSuperSimulationModalOpen(true);
      }
    }
  };

  const onClearSuperSimulation = () => {
    Analytics.sendEvent({ category: 'Profile Interaction', action: 'Rank Super Simulation', label: 'Player Profile' });
    //close button
    dispatch(clear());
    if (playerId) dispatch(compute({ playerId }));
    if (setSuperSimulationModalOpen) setSuperSimulationModalOpen(false);
  };

  const onSuperSimulationButtonClick = () => {
    Analytics.sendEvent({
      category: 'Profile Interaction',
      action: 'Rank Super Simulation Button',
      label: 'Player Profile',
    });
    if (userIsFitPlayerProfile()) {
      if (playerId) dispatch(compute({ playerId, isSuperSimulation: true }));
      if (setSuperSimulationModalOpen) setSuperSimulationModalOpen(false);
    } else {
      setModalNoCardOpen(true);
    }
  };

  const nonMemberContent = (
    <div
      id='membership'
      className='border-accent-accent-8 bg-accent-accent-4 flex flex-col flex-wrap items-center justify-center gap-2 rounded-xl border border-solid p-3'
    >
      <div id='text' className='text-center text-base font-medium'>
        Funzione disponibile solo per i Membri TennisTalker.
      </div>
      <div id='activate' className='w-full self-stretch'>
        <Button
          size='2'
          variant='outline'
          className='text-neutral-neutral-11 flex w-full items-center self-stretch hover:cursor-pointer'
          onClick={() => navigate('/club-membership')}
        >
          Attiva membership <ArrowTopRightIcon />
        </Button>
      </div>
    </div>
  );

  const memberContent = (
    <div
      id='membership'
      className='border-accent-accent-8 bg-accent-accent-4 flex flex-col flex-wrap items-center justify-center gap-2 rounded-xl border border-solid p-3'
    >
      <div id='text' className='text-center text-base font-medium'>
        Scopri la Classifica Super Simulata direttamente su questa pagina (i dati appariranno in viola)
      </div>
      <div id='activate' className='w-full self-stretch'>
        <Button
          size='2'
          variant='outline'
          className='text-neutral-neutral-11 flex w-full items-center self-stretch hover:cursor-pointer'
          onClick={onSuperSimulationButtonClick}
        >
          Calcola <StarIcon />
        </Button>
      </div>
    </div>
  );

  const modalDialog = (
    <Dialog.Root open={superSimulationModalOpen}>
      <Dialog.Content maxWidth='450px'>
        <div className='flex w-full justify-between align-middle'>
          <Dialog.Title>Scopri la Classifica Super Simulata</Dialog.Title>
          <Dialog.Close
            className='mb-4 cursor-pointer'
            onClick={() => {
              if (setSuperSimulationModalOpen) setSuperSimulationModalOpen(false);
            }}
          >
            <Cross2Icon height={'24'} width={'24'} />
          </Dialog.Close>
        </div>
        <Dialog.Description className='hidden'>Scopri la classifica super simulata del giocatore</Dialog.Description>

        <div className='flex flex-1 flex-row flex-wrap items-center justify-center gap-4'>
          <div id='container' className='flex max-w-96 grow-0 basis-96 flex-col items-start gap-4 rounded-xl p-3'>
            <div id='content' className='flex flex-col items-start gap-4 self-stretch'>
              <div className='text-neutral-neutral-12 text-sm font-normal text-wrap'>
                La funzionalità 'Classifica Super Simulata' di TennisTalker permette di calcolare i punti FITP basandosi
                sulle promozioni che avverranno al prossimo passaggio intermedio.
                <br />
                Questa simulazione non rappresenta la classifica che il giocatore otterrà al prossimo passaggio ma la
                classifica che il giocatore dovrebbe ottenere al passaggio successivo!
                <br />
                Si basa sulle classifiche che i giocatori otterranno nel prossimo passaggio intermedio e sulla
                classifica simulata del giocatore preso in considerazione e fa i calcoli su queste. (Sono esclusi dalla
                super simulazione i bonus vittoria tabelloni)
                <br />
                Nelle varie righe della tabella troverete il dettaglio di tutti i punteggi.
                <br />
                <br />
                <b>
                  NB: DOPO IL PASSAGGIO CLASSIFICHE DI META' SETTEMBRE, LA CLASSIFICA SUPERSIMULATA SARA&apos; VALIDA
                  SOLO PER CHI HA MATCH VALIDI NELL&apos;ANNO FITP 2026, PER GLI ALTRI SARA&apos; IDENTICA ALLA SIMULATA
                </b>
              </div>
            </div>
            {isCurrentUserPremium() ? memberContent : nonMemberContent}
            <div
              className='text-neutral-neutral-11 flex w-full items-center self-stretch underline hover:cursor-pointer hover:bg-transparent hover:underline-offset-2'
              onClick={() =>
                openInExternalUrl(
                  'https://magazine.tennistalker.it/2025/04/02/classifica-super-simulata-tennistalker-vantaggi/',
                )
              }
            >
              Articolo di dettaglio Classifica Super Simulata <ArrowTopRightIcon />
            </div>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );

  return (
    <div id='container' className='bg-accent-accent-6 flex flex-col items-start gap-4 self-stretch rounded-xl p-3'>
      <div id='topbar' className='flex flex-col items-start gap-2 self-stretch'>
        <div className='flex w-full flex-col items-start justify-between gap-2'>
          <div id='topbarsx' className='flex items-center gap-2'>
            <BarChartIcon width='16' height='16' />
            <div className='text-accent-accent-12 text-base leading-6 font-bold tracking-normal not-italic'>
              Classifica Supersimulata
            </div>
          </div>
          <div className='flex w-full flex-row gap-3'>
            <Badge className='bg-iris-9 flex w-auto items-center justify-start gap-1.5 rounded-md p-1 text-xs leading-4 font-medium tracking-wide text-white not-italic'>
              Esclusiva TennisTalker&#8482;&#xFE0E;
            </Badge>
          </div>
        </div>
      </div>
      <div
        id='copy'
        className='text-neutral-neutral-12 flex-1 items-center justify-between self-stretch text-sm font-normal'
      >
        {simulatorState?.isSuperSimulation ? (
          <>
            In pagina sono attualmente visibili le classifiche e i calcoli basati sui dati della Classifica Super
            Simulata, che otterrai tra 2 passaggi di classifica, cioè
            <span className='ml-1 text-lg font-bold'>{getSuperSimulationNextRankChange()}</span>.
          </>
        ) : (
          <>
            È una simulazione della classifica che otterrai tra 2 passaggi di classifica, cioè
            <span className='ml-1 text-lg font-bold'>{getSuperSimulationNextRankChange()}</span>. Si basa su dati reali
            e proiezioni delle prestazioni recenti.
          </>
        )}
      </div>

      <Button
        size={'3'}
        variant='outline'
        className='text-neutral-neutral-11 flex items-center self-stretch hover:cursor-pointer'
        onClick={onClick}
      >
        {simulatorState?.isSuperSimulation ? 'Rimuovi Dati Simulazione' : 'Classifica Supersimulata'}
      </Button>
      {modalDialog}
      <ModalNoCard
        text='Per poter vedere la classifica armonizzata aggiornata ad oggi'
        open={modalNoCardOpen}
        setOpen={setModalNoCardOpen}
      />
    </div>
  );
};

export default SuperSimulationRankBox;
